import { selectPOIs } from "@/features/survey/poisSelectors";
import {
  addCategory,
  removePOI,
  removeCategory,
  setPOIs,
  togglePOIVisibility,
} from "@/features/survey/poisSlice";
import { selectMarketMapKeys } from "@/features/survey/surveySelectors";
import { addMapKeys, deleteMapKey } from "@/features/survey/surveySlice";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import Switch from "@components/Switch";
import CategoryPin from "@components/CategoryPin";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useSortableItem } from "@hooks/useSortable";
import {
  useDeletePOICategoryMutation,
  useDeletePOIMutation,
  useUpdatePOIMutation,
} from "@services/survey-poi";
import { useDeleteSurveyMapKeyMutation } from "@services/survey-mapKeys";
import type { SurveyPOIWithCategory } from "@survey-types/survey.poi.types";
import { currentPoiId as currentPoiIdAction } from "@survey/surveySlice";
import classNames from "classnames";

interface PoiListItemProps {
  poi: SurveyPOIWithCategory;
  index: number;
}

const PoiListItem = ({ poi, index }: PoiListItemProps) => {
  const dispatch = useAppDispatch();
  const { attributes, listeners, setNodeRef, style, isDragging } =
    useSortableItem(poi.id);
  const [deletePOI] = useDeletePOIMutation();
  const [deletePOICategory] = useDeletePOICategoryMutation();
  const [deleteSurveyMapKey] = useDeleteSurveyMapKeyMutation();
  const [updatePOI] = useUpdatePOIMutation();
  const { quickDelete, withLoading } = useSimpleConfirmation();
  const allPOIs = useAppSelector(selectPOIs);
  const allMapKeys = useAppSelector(selectMarketMapKeys);

  const iconColor = poi.category?.iconData?.color ?? "#a23f97";
  const isLogo = poi.category?.pinType === "LOGO";
  const logoUrl =
    poi.category?.logoData?.logoLinks?.find((l) => l.isActive !== false)?.url ??
    poi.category?.logoData?.logoLinks?.[0]?.url;

  const handleToggleVisibility = () => {
    dispatch(togglePOIVisibility(poi.extId));
    updatePOI({
      surveyId: poi.surveyId,
      poiId: poi.extId,
      body: { isHidden: !poi.isHidden },
    }).catch((_error) => {
      console.error("Failed to update POI visibility:");
      dispatch(togglePOIVisibility(poi.extId));
    });
  };

  const handleDelete = () => {
    quickDelete({
      title: "Point of Interest",
      text: `This will remove the Point of Interest and all of its information from the survey.\nAre you sure you want to delete this Point of Interest?`,
      id: "SC-ConfirmDeletePOI",
    })
      .then((confirmed) => {
        if (confirmed) {
          const previousPOIs = allPOIs;
          const categoryId = poi.categoryId;
          const poiCategory = poi.category;

          // Determine if this category will become unused once the POI is removed
          const isCategoryUnused =
            !!categoryId &&
            !allPOIs.some(
              (p) => p.extId !== poi.extId && p.categoryId === categoryId,
            );

          // Capture the orphaned map key entry before any state changes.
          const orphanedMapKey =
            isCategoryUnused && categoryId
              ? allMapKeys.find((mk) => mk.referenceId === categoryId)
              : undefined;

          // Optimistic Redux updates
          dispatch(removePOI(poi.extId));
          if (isCategoryUnused && categoryId) {
            dispatch(removeCategory(categoryId));
          }

          withLoading(
            async () => {
              await deletePOI({
                surveyId: poi.surveyId,
                poiId: poi.extId,
              }).unwrap();

              // If the category is no longer used by any POI, delete it from the backend
              if (isCategoryUnused && categoryId) {
                await deletePOICategory({
                  surveyId: poi.surveyId,
                  categoryId,
                }).unwrap();
              }
            },
            {
              loadingText: "Deleting Point of Interest...",
              successText: "Point of Interest deleted successfully!",
              errorText: "Failed to delete Point of Interest.",
            },
          )
            .then(() => {
              // After POI + category are deleted, clean up the orphaned map key entry
              if (orphanedMapKey?.mapKeyId) {
                dispatch(deleteMapKey(orphanedMapKey.mapKeyId));
                deleteSurveyMapKey(orphanedMapKey.mapKeyId).catch(() => {
                  // If backend deletion fails (e.g. already cascade-deleted), restore in Redux
                  dispatch(addMapKeys(orphanedMapKey));
                });
              }
            })
            .catch((_error) => {
              console.error("Failed to delete POI:");
              dispatch(setPOIs(previousPOIs));
              // Restore category in Redux if it was optimistically removed
              if (isCategoryUnused && poiCategory) {
                dispatch(addCategory(poiCategory));
              }
            });
        }
      })
      .catch((_error) => {
        console.error("Error in confirmation dialog:");
      });
  };

  const handleSelectPOI = () => {
    dispatch(currentPoiIdAction({ poiId: poi.extId }));
  };

  const address = poi.addressLine1;

  return (
    <div
      id="SC-DragHandlePOI"
      ref={setNodeRef}
      style={style}
      className={classNames(
        "flex items-center gap-4 p-4 bg-primary-lighter hover:bg-divider-2 transition-colors group",
        { "opacity-50 bg-slate-100": isDragging },
      )}
    >
      {/* Drag Handle */}
      <div
        className="flex items-center text-slate-400 cursor-move"
        {...attributes}
        {...listeners}
      >
        <EmeraldIcon
          icon="drag_indicator"
          className="text-gray-400 hover:text-gray-600 transition-colors"
        />
      </div>

      {/* Order Number */}
      <div className="w-6 text-sm font-medium text-slate-600 flex items-center justify-center">
        {poi.isHidden ? "–" : index + 1}
      </div>

      {/* Category pin: logo image, icon, or color dot */}
      <CategoryPin
        pinType={isLogo ? "LOGO" : "ICON"}
        iconColor={iconColor}
        iconName={poi.category?.iconData?.iconName}
        logoUrl={logoUrl}
        size={24}
        showStroke={true}
        alt={poi.category?.categoryName ?? "POI"}
      />

      {/* POI Info */}
      <div
        className="flex-1 min-w-0 hover:cursor-pointer hover:underline"
        onClick={handleSelectPOI}
      >
        <div className="flex flex-col">
          {poi.category && (
            <div className="truncate text-base font-normal text-primary">
              {poi.category.categoryName}
            </div>
          )}
          <div className="text-base text-primary truncate">{address}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex w-52 items-center gap-x-10">
        <Switch
          checked={!poi.isHidden}
          onChange={handleToggleVisibility}
          size="small"
          label="Show"
          labelPosition="left"
        />

        <EmeraldIconButton
          icon="delete"
          type="Icon"
          className="text-data-light transition-all"
          onClick={handleDelete}
          id="SC-DeletePOI"
        />
      </div>
    </div>
  );
};

export default PoiListItem;
